import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  CharterActRefDto,
  CharterChangeDto,
  CharterTimelineEntryDto,
  CorporateCharterActSummaryDto,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CharterActItemComponent } from '@features/dashboard/company/corporate-charter/components/charter-act-item/charter-act-item.component';

interface TimelineRow {
  act: CharterActRefDto;
  summary: CorporateCharterActSummaryDto | null;
  changes: CharterChangeDto[];
  actDate: string | null;
}

/**
 * "Linha do tempo dos atos": todos os documentos lidos, do mais recente para o mais antigo, com o papel de cada um no
 * vigente, o que mudou em cada ato (`timeline` do vigente, com página e trecho) e o status da leitura (`GET acts`).
 * Junta as duas listas pelo `actId`: o ato em leitura ou com falha só existe em `acts`.
 */
@Component({
  selector: 'app-charter-acts-timeline',
  imports: [CharterActItemComponent],
  templateUrl: './charter-acts-timeline.component.html',
  styleUrl: './charter-acts-timeline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterActsTimelineComponent {
  readonly acts = input<CorporateCharterActSummaryDto[]>([]);
  readonly timeline = input<CharterTimelineEntryDto[]>([]);
  readonly missingActs = input<string[]>([]);
  /** Ato de onde vem o vigente (o mais recente aplicado, ou a base). */
  readonly currentActId = input<string | null>(null);
  readonly isInternal = input(false);
  readonly isAdmin = input(false);
  /** Documento sendo localizado no Data Room ("Abrindo..."). */
  readonly openingDocumentId = input<string | null>(null);
  /** Ato destacado pelo link `?act=` (ex.: "Abrir item" do dashboard de custos de IA, card 3411). */
  readonly highlightedActId = input<string | null>(null);

  readonly openInDataRoom = output<string>();

  /** Mais recente primeiro (data do ato, depois número). */
  protected readonly rows = computed<TimelineRow[]>(() => {
    const byId = new Map<string, TimelineRow>();
    for (const entry of this.timeline()) {
      byId.set(entry.actId, {
        act: entry,
        summary: null,
        changes: entry.changes ?? [],
        actDate: entry.actDate,
      });
    }
    for (const summary of this.acts()) {
      const row = byId.get(summary.actId);
      if (row) {
        row.summary = summary;
        row.actDate ??= summary.actDate;
      } else {
        byId.set(summary.actId, {
          act: summary,
          summary,
          changes: [],
          actDate: summary.actDate,
        });
      }
    }
    const sortKey = (row: TimelineRow) =>
      `${row.actDate ?? row.act.registeredAt ?? row.act.signedAt ?? ''}|${String(row.act.number ?? 0).padStart(6, '0')}`;
    return [...byId.values()].sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
  });
}
